import {
  BaseGridItem,
  DesktopGridItem,
  ShortcutGroup,
  ShortcutGroupBounds
} from 'shared/types/desktop-grid'

import { CellSize } from '../types/grid.types'
import { wouldCollide } from './wouldCollide'

/**
 * Height reserved above a shortcut group's members for its title bar.
 * Single source of truth for both the rendered card (ShortcutGroup) and its
 * drag placeholder (DesktopGrid) so they can never drift out of sync.
 */
export const getShortcutGroupTitleBarHeight = (
  cellSize: Pick<CellSize, 'height' | 'gapY'>
): number => (cellSize.height + cellSize.gapY) * 0.5

export type ShortcutGroupAdjacentPosition = {
  position: { row: number; col: number }
  direction: 'right' | 'below' | 'left'
}

/**
 * Returns positions that share an edge with at least one group member.
 * The order is deterministic, but callers should still rank candidates
 * against the resulting group bounds before choosing one.
 */
export const getShortcutGroupAdjacentPositions = (
  members: DesktopGridItem[],
  size: { width: number; height: number }
): ShortcutGroupAdjacentPosition[] => {
  const seen = new Set<string>()
  const positions: ShortcutGroupAdjacentPosition[] = []

  members.forEach(member => {
    const candidates: ShortcutGroupAdjacentPosition[] = [
      {
        direction: 'right',
        position: {
          row: member.position.row,
          col: member.position.col + member.size.width
        }
      },
      {
        direction: 'below',
        position: {
          row: member.position.row + member.size.height,
          col: member.position.col
        }
      },
      {
        direction: 'left',
        position: {
          row: member.position.row,
          col: member.position.col - size.width
        }
      }
    ]

    candidates.forEach(candidate => {
      const key = `${candidate.position.row},${candidate.position.col}`
      if (seen.has(key)) return
      seen.add(key)
      positions.push(candidate)
    })
  })

  return positions
}

const getRankedShortcutGroupDropCandidates = ({
  members,
  item,
  occupied,
  cols,
  rows,
  dropPosition
}: {
  members: DesktopGridItem[]
  item: DesktopGridItem
  occupied: BaseGridItem[]
  cols: number
  rows: number
  dropPosition?: { row: number; col: number }
}): Array<
  ShortcutGroupAdjacentPosition & {
    area: number
    pointerDistance: number
  }
> => {
  const directionPriority = { right: 0, below: 1, left: 2 } as const

  const distanceToCandidate = (candidate: ShortcutGroupAdjacentPosition) => {
    if (!dropPosition) return 0

    const candidateRight = candidate.position.col + item.size.width - 1
    const candidateBottom = candidate.position.row + item.size.height - 1
    const horizontalDistance =
      dropPosition.col < candidate.position.col
        ? candidate.position.col - dropPosition.col - 1
        : dropPosition.col > candidateRight
          ? dropPosition.col - candidateRight - 1
          : 0
    const verticalDistance =
      dropPosition.row < candidate.position.row
        ? candidate.position.row - dropPosition.row - 1
        : dropPosition.row > candidateBottom
          ? dropPosition.row - candidateBottom - 1
          : 0

    return horizontalDistance + verticalDistance
  }

  return getShortcutGroupAdjacentPositions(members, item.size)
    .filter(candidate => {
      const { row, col } = candidate.position
      if (
        row < 0 ||
        col < 0 ||
        row + item.size.height > rows ||
        col + item.size.width > cols
      ) {
        return false
      }
      return !wouldCollide(item.id, candidate.position, item.size, occupied)
    })
    .map(candidate => {
      const previewBounds = getShortcutGroupPreviewBounds([
        ...members,
        { ...item, position: candidate.position }
      ])
      const area = previewBounds
        ? previewBounds.size.width * previewBounds.size.height
        : Number.POSITIVE_INFINITY
      return {
        ...candidate,
        area,
        pointerDistance: distanceToCandidate(candidate)
      }
    })
    .sort(
      (a, b) =>
        a.pointerDistance - b.pointerDistance ||
        a.area - b.area ||
        directionPriority[a.direction] - directionPriority[b.direction] ||
        a.position.row - b.position.row ||
        a.position.col - b.position.col
    )
}

export const getShortcutGroupDropPosition = ({
  members,
  item,
  occupied,
  cols,
  rows,
  dropPosition
}: {
  members: DesktopGridItem[]
  item: DesktopGridItem
  occupied: BaseGridItem[]
  cols: number
  rows: number
  dropPosition?: { row: number; col: number }
}): { row: number; col: number } | null => {
  return (
    getRankedShortcutGroupDropCandidates({
      members,
      item,
      occupied,
      cols,
      rows,
      dropPosition
    })[0]?.position ?? null
  )
}

/**
 * Plans an insertion into a group. If the requested adjacent slot is already
 * occupied by group members, those members are shifted to make room, allowing
 * a shortcut to be inserted between two shortcuts.
 */
export const getShortcutGroupInsertionPlan = ({
  members,
  item,
  occupied,
  cols,
  rows,
  dropPosition,
  reorderItemId,
  reorderItemPosition
}: {
  members: DesktopGridItem[]
  item: DesktopGridItem
  occupied: BaseGridItem[]
  cols: number
  rows: number
  dropPosition?: { row: number; col: number }
  reorderItemId?: string
  reorderItemPosition?: { row: number; col: number }
}): {
  target: { row: number; col: number }
  members: DesktopGridItem[]
} | null => {
  const memberIds = new Set(members.map(member => member.id))
  const externalOccupied = occupied.filter(item => !memberIds.has(item.id))
  const reorderTarget =
    reorderItemId && dropPosition
      ? members.find(
          member =>
            dropPosition.col >= member.position.col &&
            dropPosition.col < member.position.col + member.size.width &&
            dropPosition.row >= member.position.row &&
            dropPosition.row < member.position.row + member.size.height
        )
      : undefined
  const candidates: ShortcutGroupAdjacentPosition[] = reorderTarget
    ? [{ position: { ...reorderTarget.position }, direction: 'right' }]
    : getRankedShortcutGroupDropCandidates({
        members,
        item,
        occupied: externalOccupied,
        cols,
        rows,
        dropPosition
      })
  if (candidates.length === 0) return null

  const rectanglesOverlap = (
    firstPosition: { row: number; col: number },
    firstSize: { width: number; height: number },
    secondPosition: { row: number; col: number },
    secondSize: { width: number; height: number }
  ) =>
    firstPosition.col < secondPosition.col + secondSize.width &&
    firstPosition.col + firstSize.width > secondPosition.col &&
    firstPosition.row < secondPosition.row + secondSize.height &&
    firstPosition.row + firstSize.height > secondPosition.row

  const overlaps = (
    member: DesktopGridItem,
    position: { row: number; col: number }
  ) => rectanglesOverlap(member.position, member.size, position, item.size)

  const boundaryIsClear = (
    target: { row: number; col: number },
    plannedMembers: DesktopGridItem[]
  ) => {
    const bounds = getShortcutGroupPreviewBounds([
      ...plannedMembers,
      { ...item, position: target }
    ])
    if (!bounds) return true
    const boundary = getShortcutGroupBoundaryCollisionItem(
      '__planned-shortcut-group',
      bounds
    )
    return !wouldCollide(
      boundary.id,
      boundary.position,
      boundary.size,
      externalOccupied.filter(occupiedItem => occupiedItem.id !== item.id)
    )
  }

  for (const candidate of candidates) {
    const target = candidate.position
    if (
      target.row < 0 ||
      target.col < 0 ||
      target.row + item.size.height > rows ||
      target.col + item.size.width > cols
    ) {
      continue
    }

    const collidingMembers = members.filter(member => overlaps(member, target))
    if (collidingMembers.length === 0) {
      if (!boundaryIsClear(target, members)) continue
      return { target, members }
    }

    const swapDelta =
      reorderTarget && reorderItemPosition
        ? {
            row:
              reorderItemPosition.row === target.row + item.size.height
                ? item.size.height
                : reorderItemPosition.row + item.size.height === target.row
                  ? -item.size.height
                  : 0,
            col:
              reorderItemPosition.col === target.col + item.size.width
                ? item.size.width
                : reorderItemPosition.col + item.size.width === target.col
                  ? -item.size.width
                  : 0
          }
        : null
    const delta =
      swapDelta && (swapDelta.row !== 0 || swapDelta.col !== 0)
        ? swapDelta
        : candidate.direction === 'below'
          ? { row: item.size.height, col: 0 }
          : {
              row: 0,
              col:
                candidate.direction === 'right'
                  ? item.size.width
                  : -item.size.width
            }
    const shiftedIds = new Set(collidingMembers.map(member => member.id))
    let didAddShiftedMember = true
    while (didAddShiftedMember) {
      didAddShiftedMember = false
      members.forEach(member => {
        if (shiftedIds.has(member.id)) return
        const shouldShift = members.some(shiftedMember => {
          if (!shiftedIds.has(shiftedMember.id)) return false
          const shiftedPosition = {
            row: shiftedMember.position.row + delta.row,
            col: shiftedMember.position.col + delta.col
          }
          return rectanglesOverlap(
            member.position,
            member.size,
            shiftedPosition,
            shiftedMember.size
          )
        })
        if (shouldShift) {
          shiftedIds.add(member.id)
          didAddShiftedMember = true
        }
      })
    }
    const shiftedMembers = members.map(member =>
      shiftedIds.has(member.id)
        ? {
            ...member,
            position: {
              row: member.position.row + delta.row,
              col: member.position.col + delta.col
            }
          }
        : member
    )

    if (
      shiftedMembers.some(
        member =>
          member.position.row < 0 ||
          member.position.col < 0 ||
          member.position.row + member.size.height > rows ||
          member.position.col + member.size.width > cols ||
          wouldCollide(member.id, member.position, member.size, [
            ...externalOccupied,
            ...shiftedMembers.filter(other => other.id !== member.id)
          ])
      ) ||
      wouldCollide(item.id, target, item.size, [
        ...externalOccupied,
        ...shiftedMembers
      ])
    ) {
      continue
    }

    if (!boundaryIsClear(target, shiftedMembers)) continue
    return { target, members: shiftedMembers }
  }

  return null
}

export const getShortcutGroupBounds = (
  group: ShortcutGroup,
  items: DesktopGridItem[]
): ShortcutGroupBounds | null => {
  const members = items.filter(item => group.memberIds.includes(item.id))
  if (members.length === 0) return null

  const left = Math.min(...members.map(item => item.position.col))
  const top = Math.min(...members.map(item => item.position.row))
  const right = Math.max(
    ...members.map(item => item.position.col + item.size.width)
  )
  const bottom = Math.max(
    ...members.map(item => item.position.row + item.size.height)
  )

  return {
    position: { row: top, col: left },
    size: { width: right - left, height: bottom - top }
  }
}

export const getShortcutGroupBoundaryCollisionItem = (
  groupId: string,
  bounds: ShortcutGroupBounds
): BaseGridItem => ({
  id: `__shortcut-group-boundary-${groupId}`,
  itemType: 'shortcut',
  position: {
    row: bounds.position.row - 1,
    col: bounds.position.col
  },
  size: {
    width: bounds.size.width,
    height: bounds.size.height + 1
  }
})

/**
 * Represents each group's full rectangular boundary as a collision obstacle.
 * Group members already occupy their own cells, but the boundary also blocks
 * gaps inside an irregular group shape from being filled by ungrouped items.
 * The group title reserves the cell above the member rectangle; left, right,
 * and bottom cells remain available to ungrouped shortcuts.
 */
export const getShortcutGroupCollisionItems = (
  groups: ShortcutGroup[],
  items: DesktopGridItem[],
  excludedGroupIds: string[] = []
): BaseGridItem[] => {
  const excluded = new Set(excludedGroupIds)

  return groups
    .filter(group => !excluded.has(group.id))
    .map(group => {
      const bounds = getShortcutGroupBounds(group, items)
      if (!bounds) return null

      return getShortcutGroupBoundaryCollisionItem(group.id, bounds)
    })
    .filter((item): item is BaseGridItem => item !== null)
}

export const getShortcutGroupAtCell = (
  groups: ShortcutGroup[],
  items: DesktopGridItem[],
  row: number,
  col: number,
  excludedItemIds: string[] = []
) => {
  const excluded = new Set(excludedItemIds)
  return (
    groups.find(group => {
      if (group.memberIds.some(id => excluded.has(id))) return false
      const members = items.filter(item => group.memberIds.includes(item.id))
      if (members.length === 0) return false

      return members.some(
        member =>
          col >= member.position.col &&
          col < member.position.col + member.size.width &&
          row >= member.position.row &&
          row < member.position.row + member.size.height
      )
    }) ?? null
  )
}

/**
 * Finds a group whose member is touching (or within the configured proximity
 * of) an edge of the dragged shortcut's projected rectangle. The projected
 * rectangle is used instead of the cursor because the cursor may be offset
 * anywhere inside the dragged shortcut.
 */
export const getShortcutGroupAtDropBounds = (
  groups: ShortcutGroup[],
  items: DesktopGridItem[],
  position: { row: number; col: number },
  size: { width: number; height: number },
  excludedItemIds: string[] = [],
  edgeProximity = 0
) => {
  const excluded = new Set(excludedItemIds)
  const draggedRight = position.col + size.width
  const draggedBottom = position.row + size.height
  return (
    groups.find(group => {
      if (group.memberIds.some(id => excluded.has(id))) return false

      return items
        .filter(item => group.memberIds.includes(item.id))
        .some(member => {
          const memberRight = member.position.col + member.size.width
          const memberBottom = member.position.row + member.size.height
          const horizontalOverlap =
            Math.min(draggedRight, memberRight) -
            Math.max(position.col, member.position.col)
          const verticalOverlap =
            Math.min(draggedBottom, memberBottom) -
            Math.max(position.row, member.position.row)
          const horizontalGap =
            draggedRight < member.position.col
              ? member.position.col - draggedRight
              : position.col > memberRight
                ? position.col - memberRight
                : 0
          const verticalGap =
            draggedBottom < member.position.row
              ? member.position.row - draggedBottom
              : position.row > memberBottom
                ? position.row - memberBottom
                : 0

          return (
            (horizontalOverlap > 0 && verticalGap <= edgeProximity) ||
            (verticalOverlap > 0 && horizontalGap <= edgeProximity)
          )
        })
    }) ?? null
  )
}

export const getShortcutGroupPreviewBounds = (
  previews: Array<{
    position: { row: number; col: number }
    size: { width: number; height: number }
  }>
): ShortcutGroupBounds | null => {
  if (previews.length === 0) return null

  const left = Math.min(...previews.map(item => item.position.col))
  const top = Math.min(...previews.map(item => item.position.row))
  const right = Math.max(
    ...previews.map(item => item.position.col + item.size.width)
  )
  const bottom = Math.max(
    ...previews.map(item => item.position.row + item.size.height)
  )

  return {
    position: { row: top, col: left },
    size: { width: right - left, height: bottom - top }
  }
}
